import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '../utils/cn.js';
import { Icon } from '../primitives/Icon.js';
import { V2DataGrid } from './DataGrid.js';
export function V2Button({ variant = 'secondary', size = 'md', icon, iconOnly = false, className, children, ...rest }) {
    return (_jsxs("button", { className: cn('sk-btn', `sk-btn--${variant}`, size === 'sm' && 'sk-btn--sm', size === 'lg' && 'sk-btn--lg', iconOnly && 'sk-btn--icon', className), ...rest, children: [icon ? _jsx(Icon, { name: icon, size: "sm" }) : null, iconOnly ? null : children] }));
}
export function V2Badge({ tone = 'neutral', plain = false, children, }) {
    return (_jsx("span", { className: cn('sk-badge', `sk-badge--${tone}`, plain && 'sk-badge--plain'), children: children }));
}
export function V2Tag({ children }) {
    return _jsx("span", { className: "sk-tag", children: children });
}
// ---------- Avatar ----------
export function V2Avatar({ initials, size = 'md', }) {
    return (_jsx("div", { className: cn('sk-avatar', size === 'sm' && 'sk-avatar--sm', size === 'lg' && 'sk-avatar--lg'), children: initials }));
}
// ---------- Card ----------
export function V2Card({ className, clipped = false, children, }) {
    return _jsx("div", { className: cn('sk-card', clipped && 'sk-card--clipped', className), children: children });
}
/**
 * Section-card header in the v2 Finance language: module-tinted band with a
 * pattern wash (`--sk-mod-*`, set by the nearest `[data-module]`), an
 * optional 26px icon chip, a 14px title over an 11.5px subtitle, and
 * end-aligned actions.
 */
export function V2CardHeader({ title, subtitle, actions, icon, }) {
    return (_jsxs("div", { className: "sk-card-header", children: [_jsxs("div", { className: "sk-card-header__lead", children: [icon ? (_jsx("span", { className: "sk-card-header__icon", children: _jsx(Icon, { name: icon, size: "sm" }) })) : null, _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }, children: [_jsx("div", { className: "sk-card-header__title", children: title }), subtitle ? _jsx("div", { className: "sk-card-header__subtitle", children: subtitle }) : null] })] }), actions ? _jsx("div", { className: "sk-card-header__actions", children: actions }) : null] }));
}
/** Key/value cells under a section header — e.g. a journal's date, author, approver, period. */
export function V2MetaStrip({ items, }) {
    return (_jsx("div", { className: "sk-meta-strip", children: items.map((item, i) => (_jsxs("div", { className: "sk-meta-strip__item", children: [_jsx("span", { className: "sk-meta-strip__label", children: item.label }), _jsx("span", { className: "sk-meta-strip__value", children: item.value })] }, i))) }));
}
/**
 * Status bar under a document or line-item table — "سند تراز است" /
 * "سند تراز نیست" with the exact difference, per the Finance board. A
 * validation result, never a document status.
 */
export function V2StatusStrip({ tone, title, description, valueLabel, value, action, }) {
    return (_jsxs("div", { className: `sk-status-strip sk-status-strip--${tone}`, role: "status", children: [_jsx("span", { className: "sk-status-strip__icon", children: _jsx(Icon, { name: tone === 'success' ? 'check' : tone === 'danger' ? 'x' : 'warn', size: "sm" }) }), _jsxs("div", { className: "sk-status-strip__text", children: [_jsx("span", { className: "sk-status-strip__title", children: title }), description ? _jsx("span", { className: "sk-status-strip__desc", children: description }) : null] }), value !== undefined ? (_jsxs("div", { className: "sk-status-strip__value", children: [valueLabel ? _jsx("span", { className: "sk-status-strip__value-label", children: valueLabel }) : null, _jsx("span", { className: "sk-status-strip__value-num", children: value })] })) : null, action] }));
}
export function V2CardBody({ children }) {
    return _jsx("div", { className: "sk-card-body", children: children });
}
// ---------- Kpi (StatCard) ----------
export function V2Kpi({ label, value, delta, deltaDirection, }) {
    return (_jsxs("div", { className: "sk-kpi", children: [_jsx("div", { className: "sk-kpi__label", children: label }), _jsx("div", { className: "sk-kpi__value sk-num", children: value }), delta ? (_jsxs("div", { className: cn('sk-kpi__delta', deltaDirection === 'up' && 'sk-kpi__delta--up', deltaDirection === 'down' && 'sk-kpi__delta--down'), children: [deltaDirection === 'up' ? _jsx(Icon, { name: "arrowUp", size: "sm" }) : null, delta] })) : null] }));
}
/** Row of Kpi cards in a responsive auto-fit grid — the MetricStrip composition. */
export function V2MetricStrip({ children }) {
    return (_jsx("div", { style: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'var(--sk-s-4)',
        }, children: children }));
}
export function V2Tabs({ items, activeId, onChange, }) {
    return (_jsx("div", { className: "sk-tabs", role: "tablist", style: { alignSelf: 'flex-start' }, children: items.map((item) => (_jsx("button", { type: "button", role: "tab", "aria-selected": item.id === activeId, className: cn('sk-tab', item.id === activeId && 'is-active'), onClick: () => onChange?.(item.id), children: item.label }, item.id))) }));
}
// ---------- Input group (search) ----------
export function V2SearchInput({ placeholder, value, onChange, width, }) {
    return (_jsxs("div", { className: "sk-input-group", style: width ? { width } : undefined, children: [_jsx(Icon, { name: "search", size: "sm" }), _jsx("input", { placeholder: placeholder, value: value, onChange: (event) => onChange?.(event.target.value) })] }));
}
// ---------- Progress ----------
export function V2ProgressBar({ percent }) {
    const clamped = Math.max(0, Math.min(100, percent));
    return (_jsx("div", { className: "sk-prog", children: _jsx("div", { className: "sk-prog__fill", style: { width: `${clamped}%` } }) }));
}
// ---------- Empty state ----------
export function V2EmptyState({ icon = 'info', title, description, action, compact = false, }) {
    return (_jsxs("div", { className: cn('sk-empty', compact && 'sk-empty--compact'), children: [_jsx("div", { className: "sk-empty__icon", children: _jsx(Icon, { name: icon, size: compact ? 'sm' : 'md' }) }), _jsx("div", { className: "sk-empty__title", children: title }), description ? _jsx("div", { className: "sk-empty__desc", children: description }) : null, action ? _jsx("div", { style: { marginTop: 4 }, children: action }) : null] }));
}
// ---------- Checkbox row (task lists) ----------
export function V2CheckRow({ label, meta, metaTone, checked, onChange, }) {
    return (_jsxs("div", { style: {
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 20px',
            borderBottom: '1px solid var(--sk-divider)',
        }, children: [_jsx("input", { className: "sk-check", type: "checkbox", checked: checked, onChange: (event) => onChange?.(event.target.checked) }), _jsxs("div", { style: { flex: 1 }, children: [_jsx("div", { style: { fontSize: 'var(--sk-fs-sm)', fontWeight: 500 }, children: label }), meta ? (_jsx("div", { style: {
                            fontSize: 'var(--sk-fs-2xs)',
                            color: metaTone === 'danger' ? 'var(--sk-danger)' : 'var(--sk-fg-muted)',
                        }, children: meta })) : null] })] }));
}
/**
 * Compact table for hub previews, document lines and simple lists — the
 * V2DataGrid archetype (v2 stock table) without toolbar or paging: tinted
 * sticky header, sticky first column, end-aligned numbers. Text columns
 * keep wrapping, as before. For a list with search/filters/paging use
 * V2DataGrid directly.
 */
export function V2Table({ columns, rows, density = 'compact', zebra = false, emptyState, }) {
    return (_jsx(V2DataGrid, { bare: true, densitySwitch: false, defaultDensity: density === 'comfortable' ? 'comfortable' : 'normal', pageSize: 0, maxHeight: "none", zebra: zebra, columns: columns.map((col) => ({
            key: col.key,
            header: col.header,
            render: col.render,
            ...(col.align ? { align: col.align } : {}),
            wrap: col.align !== 'end',
        })), rows: rows, rowKey: (row) => row.id, emptyState: emptyState }));
}
const FORM_FIELD_WIDTH = {
    // quantity / amount / code
    short: '120px',
    // date / select
    medium: '200px',
    // title / party / reference
    wide: '280px',
    // always its own full row (e.g. a textarea, a warning banner)
    full: '100%',
};
export function V2FormField({ label, required = false, width = 'medium', hint, error, htmlFor, children, }) {
    return (_jsxs("label", { htmlFor: htmlFor, className: error ? 'sk-form-field sk-form-field--invalid' : 'sk-form-field', "data-width": width, style: {
            flexBasis: FORM_FIELD_WIDTH[width],
            flexGrow: width === 'wide' || width === 'full' ? 1 : 0,
            width: width === 'full' ? '100%' : undefined,
        }, children: [_jsxs("span", { className: "sk-form-field__label", children: [label, required ? _jsx("span", { className: "sk-form-field__required", children: "*" }) : null] }), children, error ? (_jsx("span", { className: "sk-form-field__error", role: "alert", children: error })) : hint ? (_jsx("span", { className: "sk-form-field__hint", children: hint })) : null] }));
}
export function V2FormGrid({ children, className, }) {
    // `.sk-form-wrap` is the size container the Finance form layout keys off
    // (two-column horizontal fields from a 560px-wide form up) — see
    // components.css. Narrow containers keep the stacked layout.
    return (_jsx("div", { className: "sk-form-wrap", children: _jsx("div", { className: cn('sk-form-grid', className), children: children }) }));
}
/**
 * Row for the form's primary/secondary actions — end-aligned by default so
 * the submit button sits at the end of the field row it belongs to, not
 * detached in the page's middle. `sticky` pins it to the bottom of the
 * viewport (for a long form where the submit button would otherwise scroll
 * out of view).
 */
export function V2FormActions({ children, sticky = false, }) {
    return (_jsx("div", { className: cn('sk-form-actions', sticky && 'sk-form-actions--sticky'), children: children }));
}
export function V2AuthSurface({ logo, tenantLabel, brandHeadline, brandTagline, highlights = [], brandFooter, title, subtitle, children, footer, }) {
    return (_jsx("div", { style: {
            minHeight: '100dvh',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'radial-gradient(120% 140% at 20% -10%, color-mix(in oklch, var(--sk-brand-500), transparent 88%), var(--sk-bg-canvas) 55%)',
            padding: 'var(--sk-s-5)',
        }, children: _jsxs("div", { style: {
                width: '100%',
                maxWidth: 960,
                display: 'flex',
                flexWrap: 'wrap',
                borderRadius: 'var(--sk-r-xl)',
                overflow: 'hidden',
                boxShadow: 'var(--sk-shadow-4)',
                background: 'var(--sk-bg-elev)',
            }, children: [_jsxs("aside", { style: {
                        flex: '1 1 320px',
                        minWidth: 280,
                        padding: 'var(--sk-s-9) var(--sk-s-8)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--sk-s-7)',
                        color: 'rgba(255,255,255,0.94)',
                        background: 'linear-gradient(150deg, var(--sk-brand-700) 0%, var(--sk-brand-600) 55%, var(--sk-brand-500) 130%)',
                    }, children: [_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: 'var(--sk-s-4)' }, children: [logo ? _jsx("div", { children: logo }) : null, tenantLabel ? (_jsx("div", { "aria-label": "\u0633\u0627\u0632\u0645\u0627\u0646", style: {
                                        alignSelf: 'flex-start',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        padding: '6px 14px',
                                        borderRadius: 'var(--sk-r-pill)',
                                        background: 'rgba(255,255,255,0.14)',
                                        border: '1px solid rgba(255,255,255,0.22)',
                                        fontSize: 'var(--sk-fs-sm)',
                                        fontWeight: 600,
                                    }, children: tenantLabel })) : null] }), _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: 'var(--sk-s-3)' }, children: [_jsx("h1", { style: { margin: 0, fontSize: 'var(--sk-fs-2xl)', fontWeight: 700, lineHeight: 1.4 }, children: brandHeadline }), brandTagline ? (_jsx("p", { style: {
                                        margin: 0,
                                        fontSize: 'var(--sk-fs-sm)',
                                        lineHeight: 1.8,
                                        color: 'rgba(255,255,255,0.8)',
                                    }, children: brandTagline })) : null] }), highlights.length > 0 ? (_jsx("ul", { style: {
                                listStyle: 'none',
                                margin: 0,
                                padding: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'var(--sk-s-3)',
                            }, children: highlights.map((item, i) => (_jsxs("li", { style: {
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 10,
                                    fontSize: 'var(--sk-fs-sm)',
                                }, children: [_jsx("span", { style: {
                                            flex: '0 0 auto',
                                            display: 'flex',
                                            width: 20,
                                            height: 20,
                                            borderRadius: '50%',
                                            background: 'rgba(255,255,255,0.16)',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginTop: 1,
                                        }, children: _jsx(Icon, { name: "check", size: "sm" }) }), _jsx("span", { style: { color: 'rgba(255,255,255,0.9)', lineHeight: 1.7 }, children: item })] }, i))) })) : null, _jsx("div", { style: {
                                marginTop: 'auto',
                                fontSize: 'var(--sk-fs-xs)',
                                color: 'rgba(255,255,255,0.6)',
                            }, children: brandFooter })] }), _jsxs("div", { style: {
                        flex: '1 1 360px',
                        minWidth: 280,
                        padding: 'var(--sk-s-9) var(--sk-s-8)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: 'var(--sk-s-6)',
                    }, children: [_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: 'var(--sk-s-2)' }, children: [_jsx("h2", { style: { margin: 0, fontSize: 'var(--sk-fs-xl)', fontWeight: 700 }, children: title }), subtitle ? (_jsx("p", { style: { margin: 0, fontSize: 'var(--sk-fs-sm)', color: 'var(--sk-fg-muted)' }, children: subtitle })) : null] }), _jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: 'var(--sk-s-4)' }, children: children }), footer ? (_jsx("div", { style: { fontSize: 'var(--sk-fs-sm)', color: 'var(--sk-fg-muted)' }, children: footer })) : null] })] }) }));
}
//# sourceMappingURL=Primitives.js.map